import { useEffect, useState, useRef } from 'react'
import { MaterialIcon } from '@/ui/material-icon'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { Label } from '@/ui/label'
import { Switch } from '@/ui/switch'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useUIConfig } from '@/features/auth/hooks/useUIConfig'
import { authKeys } from '@/features/auth/api/auth.keys'
import { useUpdateBranding } from '@/features/appearence/hooks/useUpdateBranding'
import { useUploadLogo } from '@/features/appearence/hooks/useUploadLogo'
import styles from './MenuAndScreens.module.scss'

// ─── Types ────────────────────────────────────────────────────────────────────

type FieldType = 'text' | 'email' | 'password' | 'number' | 'date' | 'tel' | 'url'

interface InputFieldDef {
  id: string
  name: string
  type: FieldType
  required: boolean
  visible: boolean
  validation: {
    pattern: string
    message: string
  }
}

interface FormOptions {
  buttonText: string
  signupLink: string
  signupText: string
  showSignupPrompt: boolean
  forgotPasswordLink: string
  showForgotPassword: boolean
}

const FIELD_TYPES: FieldType[] = ['text', 'email', 'password', 'number', 'date', 'tel', 'url']

const DEFAULT_NEW_FIELD: InputFieldDef = {
  id: '',
  name: '',
  type: 'text',
  required: false,
  visible: true,
  validation: { pattern: '', message: '' },
}

const DEFAULT_FORM_OPTIONS: FormOptions = {
  buttonText: 'Sign In',
  signupLink: '/signup',
  signupText: "Don't have an account?",
  showSignupPrompt: false,
  forgotPasswordLink: '/forgot-password',
  showForgotPassword: false,
}

// ─── Utility: convert a name string to a slug id ─────────────────────────────
const toSlugId = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')

export default function MenuAndScreens() {
  return (
    <div className={styles.wrapper}>
      <div className={styles.mainContent}>
        <LoginScreenBuilder />
      </div>
    </div>
  )
}

// ─── Login Screen Builder ─────────────────────────────────────────────────────

function LoginScreenBuilder() {
  const queryClient = useQueryClient()
  const { data: uiConfig, isLoading, error } = useUIConfig()
  const updateMutation = useUpdateBranding()
  const uploadMutation = useUploadLogo()

  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(null)
  const [platformName, setPlatformName] = useState('Dhira.AI')
  const [slogan, setSlogan] = useState('Digital Human Interfaces and Research Association')
  const [emailEnabled, setEmailEnabled] = useState(true)
  const [passwordEnabled, setPasswordEnabled] = useState(true)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [showAddFieldForm, setShowAddFieldForm] = useState(false)
  const [customFields, setCustomFields] = useState<InputFieldDef[]>([])
  const [newField, setNewField] = useState<InputFieldDef>(DEFAULT_NEW_FIELD)
  const [fieldErrors, setFieldErrors] = useState<{ name?: string }>({})
  const [formOptions, setFormOptions] = useState<FormOptions>(DEFAULT_FORM_OPTIONS)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (uiConfig?.data) {
      setLogoPreview(uiConfig.data.logo || null)
      setUploadedLogoUrl(null)
      setPlatformName(uiConfig.data.ui_config.platform_name || 'Dhira.AI')
      setSlogan(uiConfig.data.ui_config.platform_slogan || 'Digital Human Interfaces and Research Association')
      const formFields = uiConfig.data.ui_config.formFields || []
      const emailField = formFields.find((f: { id: string }) => f.id === 'email')
      const passwordField = formFields.find((f: { id: string }) => f.id === 'password')
      setEmailEnabled(emailField ? emailField.isDisplay : true)
      setPasswordEnabled(passwordField ? passwordField.isDisplay : true)
    }
  }, [uiConfig])

  if (isLoading) return <div className={styles.loading}>Loading login screen config...</div>
  if (error) return <div className={styles.error}>Error loading login screen config.</div>

  const handleUploadFile = (file: File) => {
    const localPreviewUrl = URL.createObjectURL(file)
    setLogoPreview(localPreviewUrl)
    uploadMutation.mutate(file, {
      onSuccess: (url) => {
        setUploadedLogoUrl(url)
        setLogoPreview(url)
        toast.success('Logo uploaded successfully.')
      },
      onError: () => {
        toast.error('Logo upload failed. Please try again.')
      },
    })
  }

  const saveConfig = () => {
    const logoUrl = uploadedLogoUrl || logoPreview
    if (!logoUrl) {
      toast.error('Upload a logo first.')
      return
    }

    // ── Build full payload and log to console ──────────────────────────────
    const payload = {
      logo: logoUrl,
      platformName,
      slogan,
      formFields: [
        { id: 'email', name: 'Email/Mobile', type: 'email', isDisplay: emailEnabled },
        { id: 'password', name: 'Password', type: 'password', isDisplay: passwordEnabled },
        ...customFields,
      ],
      formOptions,
    }

    console.log('=== Save Screen Settings ===')
    console.log(JSON.stringify(payload, null, 2))

    updateMutation.mutate(
      {
        logo: logoUrl,
        config_json: { platform_name: platformName, platform_slogan: slogan },
        modified_by: 'tenant_admin',
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(authKeys.uiConfig() as any)
          toast.success('Login page settings saved successfully.')
        },
        onError: () => {
          toast.error('Failed to save login screen settings.')
        },
      }
    )
  }

  // ── New field helpers ──────────────────────────────────────────────────────
  const updateNewField = (key: keyof InputFieldDef, value: unknown) =>
    setNewField((prev) => {
      const updated = { ...prev, [key]: value }
      if (key === 'name') updated.id = toSlugId(value as string)
      return updated
    })

  const updateValidation = (key: 'pattern' | 'message', value: string) =>
    setNewField((prev) => ({ ...prev, validation: { ...prev.validation, [key]: value } }))

  const handleAddField = () => {
    const errs: { name?: string } = {}
    if (!newField.name.trim()) errs.name = 'Name is required'
    setFieldErrors(errs)
    if (Object.keys(errs).length > 0) return
    setCustomFields((prev) => [...prev, { ...newField, id: toSlugId(newField.name) }])
    setNewField(DEFAULT_NEW_FIELD)
    setFieldErrors({})
    setShowAddFieldForm(false)
  }

  const handleCancelAddField = () => {
    setNewField(DEFAULT_NEW_FIELD)
    setFieldErrors({})
    setShowAddFieldForm(false)
  }

  const handleRemoveCustomField = (id: string) => {
    setCustomFields((prev) => prev.filter((f) => f.id !== id))
  }

  // ── Form Options helper ────────────────────────────────────────────────────
  const updateFormOption = <K extends keyof FormOptions>(key: K, value: FormOptions[K]) =>
    setFormOptions((prev) => ({ ...prev, [key]: value }))

  return (
    <div className={styles.loginBuilder}>
      <h3 className={styles.previewTitle}>Preview</h3>
      <div className={styles.loginPreview}>

        <div className={styles.previewContainer}>


          <div className={styles.loginPreviewBox}>
            {/* Left: Logo / Name / Slogan */}
            <div className={styles.loginPreviewLeft}>
              <div className={styles.logoBox}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Platform logo" className={styles.logoImage} />
                ) : (
                  <span className={styles.logoPlaceholder}>DHIRA Logo</span>
                )}
              </div>
              <p className={styles.previewPlatformName}>{platformName}</p>
              <p className={styles.previewSlogan}>{slogan}</p>
            </div>

            {/* Right: Sign In Card */}
            <div className={styles.signInCard}>
              <h3 className={styles.signInTitle}>Sign In</h3>
              <p className={styles.signInSubtitle}>
                Enter your email &amp; password below to login to your account.
              </p>

              {emailEnabled && (
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Email/Mobile</label>
                  <div className={styles.formInput}>m@example</div>
                </div>
              )}

              {passwordEnabled && (
                <div className={styles.formField}>
                  <div className={styles.formLabelRow}>
                    <label className={styles.formLabel}>Password</label>
                    {formOptions.showForgotPassword && (
                      <span className={styles.forgotPassword}>Forgot your password?</span>
                    )}
                  </div>
                  <div className={styles.formInput}></div>
                </div>
              )}

              {customFields.filter((f) => f.visible).map((f) => (
                <div key={f.id} className={styles.formField}>
                  <label className={styles.formLabel}>
                    {f.name}{f.required && <span className={styles.requiredStar}> *</span>}
                  </label>
                  <div className={styles.formInput}></div>
                </div>
              ))}

              <button className={styles.loginButton}>{formOptions.buttonText || 'Login'}</button>

              {formOptions.showSignupPrompt && (
                <p className={styles.signupPrompt}>
                  {formOptions.signupText}{' '}
                  <span className={styles.signupLink}>Sign up</span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Settings Panel ─────────────────────────────────────────────── */}
        <div className={`${styles.settingsPanel} ${settingsOpen ? styles.settingsPanelOpen : ''}`}>

          {!settingsOpen && (
            <button
              className={styles.settingsToggle}
              onClick={() => setSettingsOpen(true)}
            >
              <span className={styles.toggleArrow}>«</span>
              <div className={styles.toggleTextContainer}>
                <span className={styles.toggleText}>Login</span>
                <span className={styles.toggleText}>Setup</span>
              </div>
            </button>
          )}

          {settingsOpen && (
            <div className={styles.settingsContent}>
              <div className={styles.settingsHeader}>
                <div className={styles.settingsHeaderContent}>
                  <Button
                    className={styles.closeButton}
                    onClick={() => setSettingsOpen(false)}
                  >
                    <span>»</span>
                  </Button>
                </div>

                <div className={styles.settingsBody}>

                  {/* ── Login Page Settings ──────────────────────────────── */}
                  <div className={styles.settingsCard}>
                    <h4 className={styles.cardTitle}>Login Page Settings</h4>

                    <div className={styles.settingSection}>
                      <Label className={styles.settingLabel}>Background Image</Label>
                      <div className={styles.uploadArea}>
                        <div
                          className={styles.uploadBox}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <span>Upload image</span>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const f = e.target.files?.[0]
                              if (f) handleUploadFile(f)
                            }}
                          />
                        </div>
                        <button
                          className={styles.browseButton}
                          onClick={() => fileInputRef.current?.click()}
                          type="button"
                        >
                          Browse files
                        </button>
                      </div>
                    </div>

                    <div className={styles.settingSection}>
                      <Label className={styles.settingLabel}>Platform Name</Label>
                      <Input
                        value={platformName}
                        className={styles.settingInput}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPlatformName(e.target.value)}
                      />
                    </div>

                    <div className={styles.settingSection}>
                      <Label className={styles.settingLabel}>Slogan/Caption</Label>
                      <Input
                        value={slogan}
                        className={styles.settingInput}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSlogan(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* ── Login Fields ─────────────────────────────────────── */}
                  <div className={styles.settingsCard}>
                    <h4 className={styles.cardTitle}>Login Fields</h4>
                    <p className={styles.cardSubtitle}>Configure login form fields</p>

                    <div className={styles.fieldToggle}>
                      <span>Email</span>
                      <Switch checked={emailEnabled} onCheckedChange={setEmailEnabled} />
                    </div>

                    <div className={styles.fieldToggle}>
                      <span>Password</span>
                      <Switch checked={passwordEnabled} onCheckedChange={setPasswordEnabled} />
                    </div>

                    {customFields.length > 0 && (
                      <div className={styles.customFieldsContainer}>
                        {customFields.map((f) => (
                          <div key={f.id} className={styles.customFieldCard}>
                            <div className={styles.customFieldInfo}>
                              <span className={styles.customFieldName}>{f.name}</span>
                              <span className={styles.customFieldMeta}>
                                <span className={styles.customFieldId}>id: {f.id}</span>
                                <span className={styles.customFieldType}>{f.type}</span>
                              </span>
                            </div>
                            <button
                              className={styles.removeFieldBtn}
                              onClick={() => handleRemoveCustomField(f.id)}
                            >
                              <MaterialIcon name="close" size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <Button
                      variant="outline"
                      className={styles.addFieldBtn}
                      onClick={() => setShowAddFieldForm(!showAddFieldForm)}
                    >
                      <MaterialIcon name={showAddFieldForm ? 'remove' : 'add'} size={16} />
                      {showAddFieldForm ? 'Cancel' : 'Add input fields'}
                    </Button>

                    {showAddFieldForm && (
                      <div className={styles.addFieldForm}>
                        {/* Name first — drives auto-ID */}
                        <div className={styles.formRow}>
                          <Label className={styles.formRowLabel}>Feild Label </Label>
                          <Input
                            value={newField.name}
                            placeholder="e.g., Phone Number"
                            className={styles.formRowInput}
                            onChange={(e) => updateNewField('name', e.target.value)}
                          />
                          {fieldErrors.name && <span className={styles.errorText}>{fieldErrors.name}</span>}
                        </div>

                        {/* ID — read-only, auto-derived from name */}
                        <div className={styles.formRow}>
                          <Label className={styles.formRowLabel}>
                            ID
                            <span className={styles.autoLabel}> (auto-generated from label)</span>
                          </Label>
                          <Input
                            value={toSlugId(newField.name)}
                            disabled
                            className={`${styles.formRowInput} ${styles.disabledInput}`}
                            placeholder="auto"
                          />
                        </div>

                        <div className={styles.formRow}>
                          <Label className={styles.formRowLabel}>Type</Label>
                          <select
                            value={newField.type}
                            className={styles.selectInput}
                            onChange={(e) => updateNewField('type', e.target.value as FieldType)}
                          >
                            {FIELD_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className={styles.checkboxGroup}>
                          <label className={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={newField.required}
                              onChange={(e) => updateNewField('required', e.target.checked)}
                            />
                            Required
                          </label>
                          <label className={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={newField.visible}
                              onChange={(e) => updateNewField('visible', e.target.checked)}
                            />
                            Visible
                          </label>
                        </div>

                        <div className={styles.validationSection}>
                          <Label className={styles.formRowLabel}>Validation Pattern</Label>
                          <Input
                            value={newField.validation.pattern}
                            placeholder="e.g., ^[0-9]{10}$"
                            className={styles.formRowInput}
                            onChange={(e) => updateValidation('pattern', e.target.value)}
                          />
                          <Input
                            value={newField.validation.message}
                            placeholder="Validation error message"
                            className={styles.formRowInput}
                            onChange={(e) => updateValidation('message', e.target.value)}
                          />
                        </div>

                        <div className={styles.formActions}>
                          <Button variant="outline" onClick={handleCancelAddField}>
                            Cancel
                          </Button>
                          <Button onClick={handleAddField}>
                            Add Field
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Form Options ──────────────────────────────────────── */}
                  <div className={styles.settingsCard}>
                    <h4 className={styles.cardTitle}>Form Options</h4>
                    <p className={styles.cardSubtitle}>Configure form behaviour and links</p>

                    {/* Button Text */}
                    <div className={styles.settingSection}>
                      <Label className={styles.settingLabel}>Button Text</Label>
                      <Input
                        value={formOptions.buttonText}
                        className={styles.settingInput}
                        placeholder="e.g., Sign In"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateFormOption('buttonText', e.target.value)
                        }
                      />
                    </div>

                    {/* Forgot Password */}
                    <div className={styles.optionGroup}>
                      <div className={styles.optionGroupHeader}>
                        <div className={styles.optionGroupLabel}>
                          <span className={styles.settingLabel}>Forgot Password</span>
                          <p className={styles.optionGroupHint}>Show a "Forgot password?" link on the form</p>
                        </div>
                        <Switch
                          checked={formOptions.showForgotPassword}
                          onCheckedChange={(val) => updateFormOption('showForgotPassword', val)}
                        />
                      </div>

                      {formOptions.showForgotPassword && (
                        <div className={styles.optionGroupBody}>
                          <Label className={styles.formRowLabel}>Forgot Password Link</Label>
                          <Input
                            value={formOptions.forgotPasswordLink}
                            className={styles.formRowInput}
                            placeholder="/forgot-password"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateFormOption('forgotPasswordLink', e.target.value)
                            }
                          />
                        </div>
                      )}
                    </div>

                    {/* Sign Up Prompt */}
                    <div className={styles.optionGroup}>
                      <div className={styles.optionGroupHeader}>
                        <div className={styles.optionGroupLabel}>
                          <span className={styles.settingLabel}>Sign Up Prompt</span>
                          <p className={styles.optionGroupHint}>Show a sign-up nudge below the login button</p>
                        </div>
                        <Switch
                          checked={formOptions.showSignupPrompt}
                          onCheckedChange={(val) => updateFormOption('showSignupPrompt', val)}
                        />
                      </div>

                      {formOptions.showSignupPrompt && (
                        <div className={styles.optionGroupBody}>
                          <Label className={styles.formRowLabel}>Sign Up Text</Label>
                          <Input
                            value={formOptions.signupText}
                            className={styles.formRowInput}
                            placeholder="Don't have an account?"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateFormOption('signupText', e.target.value)
                            }
                          />
                          <Label className={styles.formRowLabel} style={{ marginTop: 8 }}>
                            Sign Up Link
                          </Label>
                          <Input
                            value={formOptions.signupLink}
                            className={styles.formRowInput}
                            placeholder="/signup"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateFormOption('signupLink', e.target.value)
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Save */}
                  <div className={styles.saveButtonContainer}>
                    <Button
                      className={styles.saveButton}
                      onClick={saveConfig}
                      disabled={updateMutation.isPending || uploadMutation.isPending}
                    >
                      {updateMutation.isPending ? 'Saving...' : 'Save Screen Settings'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}